import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { Project, ProjectProjection, AppConfig } from '../types';

interface ExportContext {
    project: Project;
    config: AppConfig;
    projection?: ProjectProjection;
}

export const exportProjectToExcel = async (context: ExportContext) => {
    const { project } = context;

    try {
        // 1. Load the template
        const response = await fetch('/assets/template.xlsx');
        const arrayBuffer = await response.arrayBuffer();

        // 2. Create workbook from template
        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(arrayBuffer);

        // Find the template sheet 'Calculo'
        let templateSheet = workbook.getWorksheet('Calculo');
        if (!templateSheet) {
            // Fallback to second sheet if name differs
            templateSheet = workbook.worksheets[1];
        }

        if (!templateSheet) {
            throw new Error('Template sheet "Calculo" not found');
        }

        // 3. Process each resource
        for (const resource of project.resources) {
            const sheetName = resource.name.substring(0, 30) || 'Resource';
            const newSheet = workbook.addWorksheet(sheetName);

            copySheet(templateSheet, newSheet);

            const writeVal = (row: number, valCOP: number, valUSD: number, rate?: number) => {
                newSheet.getCell(`C${row}`).value = valCOP;
                newSheet.getCell(`D${row}`).value = valUSD;
                if (rate !== undefined && rate !== null && rate !== 0) {
                    newSheet.getCell(`B${row}`).value = rate;
                    newSheet.getCell(`B${row}`).numFmt = '0.00%';
                } else if (rate === 0) {
                    newSheet.getCell(`B${row}`).value = 0;
                    newSheet.getCell(`B${row}`).numFmt = '0.00%';
                }
            };

            // Write Applicant Name if available
            if (resource.applicantName) {
                newSheet.getCell('C3').value = resource.applicantName; // Assuming C3 is a good place for "Nombre"
            }

            // Helper to find concept values
            const getConceptVal = (name: string) => {
                const concept = resource.monthlyCTC.concepts.find(c => c.name === name);
                return {
                    cop: concept ? concept.amountCOP : 0,
                    usd: concept ? concept.amountUSD : 0,
                    rate: concept ? concept.percentage : undefined
                };
            };

            // Exchange Rate (TRM) was removed from header per user request to avoid clutter


            // Row 4: Salary
            const salary = getConceptVal('Salary');
            writeVal(4, salary.cop, salary.usd, salary.rate);

            // Row 5: Connectivity Assistance
            const connectivity = getConceptVal('Connectivity assistance');
            writeVal(5, connectivity.cop, connectivity.usd, connectivity.rate);

            // Row 6: Unemployment Fund
            const cesantias = getConceptVal('Unemployment Fund');
            writeVal(6, cesantias.cop, cesantias.usd, cesantias.rate);

            // Row 7: Interest Unemployment Fund
            const intereses = getConceptVal('Interest Unemployment Fund');
            writeVal(7, intereses.cop, intereses.usd, intereses.rate);

            // Row 8: Vacation
            const vacation = getConceptVal('Vacation');
            writeVal(8, vacation.cop, vacation.usd, vacation.rate);

            // Row 9: Salary 13
            const prima = getConceptVal('Salary 13');
            writeVal(9, prima.cop, prima.usd, prima.rate);

            // Row 10: Health System
            const salud = getConceptVal('Health System');
            writeVal(10, salud.cop, salud.usd, salud.rate);

            // Row 11: Retirement Fund
            const pension = getConceptVal('Retirement Fund');
            writeVal(11, pension.cop, pension.usd, pension.rate);

            // Row 12: Labor Risk Insurance
            const arl = getConceptVal('Labor Risk Insurance');
            writeVal(12, arl.cop, arl.usd, arl.rate);

            // Row 13: Caja de compensacion
            const caja = getConceptVal('Contribucions by employer (Caja de compensacion Familiar)');
            writeVal(13, caja.cop, caja.usd, caja.rate);

            // Row 14: Sena
            const sena = getConceptVal('Contribucions by employer (Sena)');
            writeVal(14, sena.cop, sena.usd, sena.rate);

            // Row 15: ICBF
            const icbf = getConceptVal('Contribucions by employer (Icbf)');
            writeVal(15, icbf.cop, icbf.usd, icbf.rate);

            // Row 16: Severance
            const severance = getConceptVal('Severance');
            writeVal(16, severance.cop, severance.usd, severance.rate);

            // Row 17: Life insurance
            const life = getConceptVal('Life insurance');
            writeVal(17, life.cop, life.usd);

            // Row 18: Medicina Prepagada
            const prepagada = getConceptVal('Medicina Prepagada');
            writeVal(18, prepagada.cop, prepagada.usd);

            // Row 19: Sub Total Cost of Law
            const costOfLaw = getConceptVal('Sub Total: Cost of law');
            writeVal(19, costOfLaw.cop, costOfLaw.usd);

            // Row 20: Recruiting (Hiring)
            const hiring = getConceptVal('Hiring');
            writeVal(20, hiring.cop, hiring.usd);

            // Row 21: Safety Study
            const safety = getConceptVal('Safety Study');
            writeVal(21, safety.cop, safety.usd);

            // Row 22: Laptop Rental
            const laptop = getConceptVal('Laptop Rental');
            writeVal(22, laptop.cop, laptop.usd);

            // Row 23: Delivery Support Cost
            const delivery = getConceptVal('Delivery support cost');
            writeVal(23, delivery.cop, delivery.usd, delivery.rate);

            // Row 24: Sub Total Cost
            const subTotal = getConceptVal('Sub Total Cost');
            writeVal(24, subTotal.cop, subTotal.usd);

            // Row 25: Financing
            const financing = getConceptVal('Financing');
            writeVal(25, financing.cop, financing.usd, financing.rate);

            // Row 26: G&A Colombia
            const ga = getConceptVal('G&A Colombia');
            writeVal(26, ga.cop, ga.usd, ga.rate);

            // Row 27: Management Fees
            const fees = getConceptVal('Management Fees');
            writeVal(27, fees.cop, fees.usd, fees.rate);

            // Row 28: Total Cost
            const total = getConceptVal('Total Cost');
            writeVal(28, total.cop, total.usd);

        }

        // 5. Cleanup
        const templateSheetId = templateSheet.id;
        const sheet1 = workbook.worksheets[0];
        if (sheet1) workbook.removeWorksheet(sheet1.id);
        workbook.removeWorksheet(templateSheetId);

        // 6. Download
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(blob, `CTC_Export_${project.name}_${new Date().toISOString().split('T')[0]}.xlsx`);

    } catch (error) {
        console.error('Error exporting to Excel:', error);
        throw error;
    }
};

// Helper to copy sheet content (naive implementation for structure/styles)
function copySheet(source: ExcelJS.Worksheet, target: ExcelJS.Worksheet) {
    // Copy properties
    // Copy properties manually to avoid circular references
    if (source.properties) {
        target.properties = { ...source.properties };
    }

    if (source.pageSetup) {
        target.pageSetup = { ...source.pageSetup };
    }

    // Copy columns manually
    if (source.columns) {
        target.columns = source.columns.map(col => ({
            header: col.header,
            key: col.key,
            width: col.width,
            style: col.style,
            hidden: col.hidden,
            outlineLevel: col.outlineLevel
        }));
    }

    // Copy rows
    source.eachRow({ includeEmpty: true }, (row, rowNumber) => {
        const targetRow = target.getRow(rowNumber);

        // Copy row properties
        targetRow.height = row.height;
        targetRow.hidden = row.hidden;

        row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
            const targetCell = targetRow.getCell(colNumber);

            // Copy value - direct assignment is usually safe for primitives
            targetCell.value = cell.value;

            // Copy style - avoid JSON.stringify if possible
            if (cell.style) {
                // Partial copy of style properties we care about
                targetCell.style = {
                    numFmt: cell.style.numFmt,
                    font: cell.style.font,
                    alignment: cell.style.alignment,
                    border: cell.style.border,
                    fill: cell.style.fill
                };
            }

            // Copy formatting directly if needed (usually covered by style)
            if (cell.numFmt) targetCell.numFmt = cell.numFmt;
        });
        targetRow.commit();
    });

    // Merges
    // source.merges is an object in some versions or we can use specific function
    // For ExcelJS v4+
    // @ts-ignore
    if (source._merges) {
        // @ts-ignore
        for (const merge of Object.values(source._merges)) {
            // @ts-ignore
            target.mergeCells(merge.model);
        }
    }
}
